import type { OperationVariables } from "@apollo/client";
// eslint-disable-next-line no-restricted-imports -- this module is the one place allowed to wrap it.
import { useMutation as useApolloMutation } from "@apollo/client/react";

type Execute = (options?: { onError?: unknown }) => unknown;

/**
 * One wrapper per Apollo execute function, per whether the hook has an
 * `onError`. Apollo's execute function is stable across renders, so this keeps
 * the wrapper stable too, for the effects and callbacks that list it as a
 * dependency. A WeakMap rather than `useCallback`, because some tests call a
 * component as a plain function, outside any render.
 */
const wrapped = {
	withHookOnError: new WeakMap<Execute, Execute>(),
	withoutHookOnError: new WeakMap<Execute, Execute>(),
};

function keepOnErrorContract(
	execute: Execute,
	hasHookOnError: boolean,
): Execute {
	const cache = hasHookOnError
		? wrapped.withHookOnError
		: wrapped.withoutHookOnError;
	let wrapper = cache.get(execute);
	if (!wrapper) {
		wrapper = (executeOptions) => {
			const pending = execute(executeOptions);
			const handled = hasHookOnError || Boolean(executeOptions?.onError);
			if (!handled || !(pending instanceof Promise)) {
				return pending;
			}
			return pending.catch((error: unknown) => ({ data: undefined, error }));
		};
		cache.set(execute, wrapper);
	}
	return wrapper;
}

/**
 * Apollo's `useMutation`, keeping Apollo Client 3's contract for `onError`.
 *
 * In Apollo Client 3 a mutation that failed with an `onError` handler resolved
 * — with `data` undefined — because the handler had dealt with the error.
 * Apollo Client 4 calls the handler and then rejects anyway. The app was written
 * against the first contract in hundreds of places: a dialog that awaits the
 * mutation after handing its error to `onError`, a submit that reads `data` to
 * decide whether to roll back. Under 4 each of those throws an uncaught error
 * past the handler it already has.
 *
 * With an `onError` — on the hook or on the call — a failed mutation resolves
 * with `{ data: undefined, error }`. Without one it rejects, as it always did.
 */
export const useMutation = ((
	mutation: unknown,
	options?: { onError?: unknown },
) => {
	const [execute, result] = (
		useApolloMutation as unknown as (...args: unknown[]) => [Execute, unknown]
	)(mutation, options);
	if (typeof execute !== "function") {
		return [execute, result];
	}
	return [keepOnErrorContract(execute, Boolean(options?.onError)), result];
}) as typeof useApolloMutation;

/** The option and result types of Apollo's hook, which this one shares. */
// eslint-disable-next-line @typescript-eslint/no-namespace -- declaration merging mirrors Apollo's useMutation namespace
export declare namespace useMutation {
	export type Options<
		TData = unknown,
		TVariables extends OperationVariables = OperationVariables,
	> = useApolloMutation.Options<TData, TVariables>;
	export type ResultTuple<
		TData,
		TVariables extends OperationVariables,
	> = useApolloMutation.ResultTuple<TData, TVariables>;
}
